import { useEffect, useState } from 'react';
import { Box, Button, Text, useToast } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export function PWAInstall() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const toast = useToast();
  const { t } = useTranslation(['v0/common']);

  useEffect(() => {
    console.log('PWAInstall 组件已加载，等待 beforeinstallprompt 事件...');

    const handler = (e: Event) => {
      // 阻止 Chrome 67+ 自动显示安装提示
      e.preventDefault();
      console.log('捕获到 beforeinstallprompt 事件', e);

      // 存储事件以便稍后触发
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setIsVisible(true);
    };

    window.addEventListener('beforeinstallprompt', handler);

    // 检查是否已经在独立模式运行
    import('../../utils/platformUtils').then(({ isStandaloneMode }) => {
      if (isStandaloneMode()) {
        console.log('应用已经以 PWA 模式运行，不显示安装提示');
        setIsVisible(false);
      } else {
        console.log('应用以普通网页模式运行，可能会显示安装提示');
      }
    });

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
    };
  }, []);

  const installApp = async () => {
    if (!deferredPrompt) {
      console.warn('尝试安装 PWA，但 deferredPrompt 为空');
      return;
    }

    console.log('显示 PWA 安装提示');

    try {
      // 显示安装提示
      deferredPrompt.prompt();

      // 等待用户响应
      const { outcome } = await deferredPrompt.userChoice;
      console.log('用户安装选择结果:', outcome);

      if (outcome === 'accepted') {
        toast({
          title: t('pwaInstall.successTitle', '安装成功'),
          description: t('pwaInstall.successDescription', '应用已成功安装到您的设备'),
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      } else {
        toast({
          title: t('pwaInstall.cancelTitle', '安装已取消'),
          description: t('pwaInstall.cancelDescription', '您可以稍后再安装'),
          status: "info",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('PWA 安装过程中出错:', error);
      toast({
        title: t('pwaInstall.errorTitle', '安装失败'),
        description: t('pwaInstall.errorDescription', '安装过程中出现错误，请稍后再试'),
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }

    // 清除提示，确保只使用一次
    setDeferredPrompt(null);
    setIsVisible(false);
  };

  const dismissInstall = () => {
    console.log('用户关闭了 PWA 安装提示');
    setIsVisible(false);
  };

  // 手动触发安装（用于调试）
  const manualInstall = () => {
    if ('standalone' in window.navigator && (window.navigator as any).standalone === false) {
      // iOS 设备
      toast({
        title: t('pwaInstall.iosInstructions', 'iOS 安装说明'),
        description: t('pwaInstall.iosInstructionsDetail', '请点击分享按钮，然后选择"添加到主屏幕"'),
        status: "info",
        duration: 5000,
        isClosable: true,
      });
    } else if (deferredPrompt) {
      // 有安装事件，正常安装
      installApp();
    } else {
      // 没有安装事件，可能是已安装或不支持
      toast({
        title: t('pwaInstall.notAvailable', '无法安装'),
        description: t('pwaInstall.notAvailableDetail', '您的浏览器可能不支持 PWA 安装，或应用已经安装'),
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  if (!isVisible) return null;

  return (
    <Box
      position="fixed"
      bottom="56px"
      left="0"
      right="0"
      p={4}
      bg="white"
      boxShadow="0 -2px 10px rgba(0, 0, 0, 0.1)"
      zIndex="1000"
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      gap={2}
    >
      <Text fontSize="sm">{t('pwaInstall.description', '将应用安装到您的设备，获得更好的体验')}</Text>
      <Box display="flex" gap={2}>
        <Button size="sm" colorScheme="blue" onClick={installApp}>
          {t('pwaInstall.installButton', '安装应用')}
        </Button>
        <Button size="sm" variant="ghost" onClick={dismissInstall}>
          {t('pwaInstall.cancelButton', '稍后再说')}
        </Button>
      </Box>
    </Box>
  );
}